// Library Methods
import React, { useCallback, useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import i18next from "i18next";
import { DateTime } from "luxon";

// MUI Components
import { Button, FormControl, Grid } from "@mui/material";
import { LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterLuxon } from "@mui/x-date-pickers/AdapterLuxon";

// Components
import CustomDatePicker from "../../../../components/UI/CustomDatePIcker";
import SearchBox from "../../../../components/UI/SearchBox";
import DefaultDrawer from "../../../../components/UI/DefaultDrawer";
import ExamCardList from "../ExamCardList";

// Utilities
import { getLocale } from "../../../../utils/localeHelper";
import {
  CustomTextFieldOutlined,
  ModalCustomCancel,
  ModalCustomConfirm,
} from "../../../../styles/muiStylesHelper";
import { getLocalDate } from "../../../../utils/dateHelper";
import { useExamListContext } from "../../../../contexts/ExamListContext";
import { getTruncatedText } from "../../../../utils/textHelper";
import useClinic from "../../../../hooks/useClinic";

const EditExamSlider = ({
  isLoading,
  visible,
  onClose,
  onConfirm,
  patient = {},
}) => {
  // internationalization
  const { t } = useTranslation();
  const { clinicTimezone } = useClinic();

  const {
    devicesOptions,
    scheduledDate,
    scheduledError,
    examCards,
    editExamDevice,
    actions,
  } = useExamListContext();
  const { setEditExamDevice, setScheduledDate, setScheduledError } = actions;

  const selectedExamCards = useMemo(
    () => examCards.selected,
    [examCards.selected]
  );

  const formattedDevicesOptions = useMemo(
    () =>
      devicesOptions?.map((option) => {
        return { name: option?.alias, val: option?.id };
      }),
    [devicesOptions]
  );

  const shouldDisable = useMemo(() => {
    let isIncluded = false;
    devicesOptions?.forEach((option) => {
      if (option?.id === editExamDevice?.val) {
        isIncluded = true;
      }
    });

    const examHasInvalidValues = selectedExamCards.some((examCard) => {
      const { examType, eyeCheckboxes } = examCard;
      return !(examType && (eyeCheckboxes?.left || eyeCheckboxes?.right));
    });

    return Boolean(
      isLoading ||
        scheduledError ||
        !scheduledDate ||
        !selectedExamCards.length ||
        examHasInvalidValues ||
        (editExamDevice?.val && editExamDevice?.val !== "" && !isIncluded)
    );
  }, [
    isLoading,
    devicesOptions,
    editExamDevice?.val,
    selectedExamCards,
    scheduledError,
    scheduledDate,
  ]);

  useEffect(() => {
    const handleEscKey = (event) => {
      if (event.key === "Escape") {
        onClose();
      }
    };

    if (visible) {
      document.addEventListener("keydown", handleEscKey);
    } else {
      document.removeEventListener("keydown", handleEscKey);
    }

    return () => {
      document.removeEventListener("keydown", handleEscKey);
    };
  }, [visible, onClose]);

  const handleChange = useCallback(
    (newValue) => {
      // Set the new value to the correct timezone and start of the day
      const newValueInZone = newValue?.setZone(clinicTimezone)?.startOf("day");

      // Set local time to the start of the day
      const today = DateTime.now().setZone(clinicTimezone).startOf("day");

      if (
        newValueInZone &&
        newValueInZone?.isValid &&
        newValueInZone >= today
      ) {
        setScheduledError(false);
        setScheduledDate(newValueInZone);
      } else {
        setScheduledError(true);
      }
    },
    [setScheduledDate, setScheduledError, clinicTimezone]
  );

  // truncated name
  const patientName = useMemo(
    () => getTruncatedText(`${patient?.firstName} ${patient?.lastName}`),
    [patient]
  );

  return (
    <DefaultDrawer
      isOpen={visible}
      onClose={onClose}
      showCloseButton={false}
      title={`${t("exams_edit_modal_title")} / ${patientName} / ${getLocalDate(patient?.dateOfBirth, clinicTimezone)}`}
      customStyles={{
        width: { xs: "100%", md: "80%" },
        height: "100%",
      }}
    >
      <Grid
        position="sticky"
        top="72px"
        zIndex={100}
        backgroundColor="#FFF"
        display="flex"
        justifyContent="space-between"
        columnGap="20px"
        padding="20px 20px 0px 20px"
        borderBottom="1px solid #435B6D7A"
      >
        <Grid
          display="flex"
          columnGap="20px"
          flexDirection={{ xs: "column", sm: "row" }}
          float={{ xs: "end", sm: "right" }}
          width={{ xs: "100%", sm: "450px" }}
        >
          <FormControl
            fullWidth
            sx={() =>
              CustomTextFieldOutlined({
                mb: "1.25rem",
              })
            }
            color="warning"
          >
            {/* Scheduled Date */}
            <LocalizationProvider
              dateAdapter={AdapterLuxon}
              adapterLocale={getLocale(i18next.language).locale}
            >
              <CustomDatePicker
                date={scheduledDate}
                labelTitle="exams_modal_scheduled_date"
                height={30}
                scheduledError={scheduledError}
                handleChange={handleChange}
                clinicTimezone={clinicTimezone}
              />
            </LocalizationProvider>
          </FormControl>

          {/* Device selector */}
          <SearchBox
            data={formattedDevicesOptions}
            state={editExamDevice}
            setState={setEditExamDevice}
            propertyBit={4}
            label={
              formattedDevicesOptions?.length
                ? t("device_link_modal_devices")
                : t("device_unavailable_modal_title")
            }
          />
        </Grid>

        <Grid
          sx={{
            display: "flex",
            flexDirection: { xs: "column", sm: "row" },
            justifyContent: "flex-start",
            gap: "20px",
            minWidth: "fit-content",
          }}
        >
          <Button
            variant="text"
            disabled={isLoading}
            sx={ModalCustomCancel}
            onClick={onClose}
          >
            {t("button_cancel")}
          </Button>

          <Button
            variant="outlined"
            sx={ModalCustomConfirm}
            disabled={shouldDisable}
            onClick={onConfirm}
          >
            {isLoading ? t("word_loading") : t("button_save_changes")}
          </Button>
        </Grid>
      </Grid>

      <ExamCardList />
    </DefaultDrawer>
  );
};

export default EditExamSlider;
